const DEFAULT_TIMEOUT_MS = 12_000;
let requestSequence = 0;

interface AutopopulateResponse {
  requestId: string;
  ok: boolean;
  playerIds?: unknown;
  unrankedPlayerIds?: unknown;
  recognizedPlayerIds?: unknown;
  unknownPlayerIds?: unknown;
  keeperCapacity?: unknown;
  targetCount?: unknown;
  error?: unknown;
}

export interface AutopopulateWorker {
  postMessage: (message: unknown) => void;
  terminate: () => void;
  addEventListener: (
    type: "message" | "error" | "messageerror",
    listener: EventListener,
  ) => void;
  removeEventListener: (
    type: "message" | "error" | "messageerror",
    listener: EventListener,
  ) => void;
}

export type AutopopulateWorkerFactory = () => AutopopulateWorker;

function defaultWorkerFactory(): AutopopulateWorker {
  return new Worker(
    new URL("./keeper-autopopulate.worker.ts", import.meta.url),
    { type: "module", name: "team-beer-keeper-autopopulate" },
  ) as unknown as AutopopulateWorker;
}

function validatedPlayerIds(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) {
    return null;
  }
  const unique = [...new Set(value)];
  return unique.length === value.length ? unique : null;
}

function isOrderedSubset(values: readonly string[], source: readonly string[]): boolean {
  let sourceIndex = 0;
  for (const value of values) {
    while (sourceIndex < source.length && source[sourceIndex] !== value) sourceIndex += 1;
    if (sourceIndex >= source.length) return false;
    sourceIndex += 1;
  }
  return true;
}

/**
 * Run one exact Draft Lab recommendation pass in a fresh, isolated worker.
 * The worker is always terminated after this request so its request-specific
 * eligibility mutation can never leak into another manager's calculation.
 */
export function draftLabAutopopulatePlayerIdsWithWorker(
  teamName: string,
  eligiblePlayerIds: readonly string[],
  preservedPlayerIds: readonly string[],
  expectedKeeperCapacity: number,
  workerFactory: AutopopulateWorkerFactory = defaultWorkerFactory,
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<string[]> {
  return new Promise<string[]>((resolve, reject) => {
    if (!Number.isInteger(expectedKeeperCapacity) || expectedKeeperCapacity <= 0) {
      reject(new Error("The keeper limit is invalid for Draft Lab recommendations."));
      return;
    }
    let worker: AutopopulateWorker;
    try {
      worker = workerFactory();
    } catch (caught) {
      reject(caught instanceof Error
        ? caught
        : new Error("The Draft Lab recommendation worker could not start."));
      return;
    }

    const requestEligiblePlayerIds = [...new Set(eligiblePlayerIds)];
    const requestEligibleSet = new Set(requestEligiblePlayerIds);
    const requestPreservedPlayerIds = [...new Set(preservedPlayerIds)];
    if (requestPreservedPlayerIds.some((playerId) => !requestEligibleSet.has(playerId))) {
      worker.terminate();
      reject(new Error("A preserved keeper is not in the current eligibility list."));
      return;
    }

    const requestId = `keeper-autopopulate-${Date.now()}-${requestSequence += 1}`;
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      worker.removeEventListener("message", onMessage);
      worker.removeEventListener("error", onError);
      worker.removeEventListener("messageerror", onMessageError);
      worker.terminate();
      callback();
    };
    const onMessage = ((event: MessageEvent<AutopopulateResponse>) => {
      const response = event.data;
      if (!response || response.requestId !== requestId) return;
      const playerIds = validatedPlayerIds(response.playerIds);
      const unrankedPlayerIds = validatedPlayerIds(response.unrankedPlayerIds);
      const recognizedPlayerIds = validatedPlayerIds(response.recognizedPlayerIds);
      const unknownPlayerIds = validatedPlayerIds(response.unknownPlayerIds);
      const keeperCapacity = response.keeperCapacity;
      const targetCount = response.targetCount;
      if (
        response.ok === true
        && playerIds
        && unrankedPlayerIds
        && recognizedPlayerIds
        && unknownPlayerIds
        && Number.isInteger(keeperCapacity)
        && (keeperCapacity as number) >= 0
        && Number.isInteger(targetCount)
        && (targetCount as number) >= 0
      ) {
        const rankedSet = new Set(playerIds);
        const unrankedSet = new Set(unrankedPlayerIds);
        const recognizedSet = new Set(recognizedPlayerIds);
        const unknownSet = new Set(unknownPlayerIds);
        const expectedUnranked = requestEligiblePlayerIds
          .filter((playerId) => !rankedSet.has(playerId));
        const recognizedPreserved = requestPreservedPlayerIds
          .filter((playerId) => recognizedSet.has(playerId))
          .slice(0, targetCount as number);
        const expectedUnknown = requestEligiblePlayerIds
          .filter((playerId) => !recognizedSet.has(playerId));
        const validContract = (
          playerIds.length === targetCount
          && keeperCapacity === expectedKeeperCapacity
          && targetCount === Math.min(expectedKeeperCapacity, recognizedPlayerIds.length)
          && playerIds.every((playerId) => requestEligibleSet.has(playerId) && recognizedSet.has(playerId))
          && recognizedPlayerIds.every((playerId) => requestEligibleSet.has(playerId))
          && isOrderedSubset(recognizedPlayerIds, requestEligiblePlayerIds)
          // The League Hub ships the complete canonical Draft Lab catalog. If
          // any current eligible id is unknown to that bundled catalog, exact
          // Draft Lab parity cannot be guaranteed. Fail closed rather than
          // silently filling slots in server order under a success message.
          && unknownPlayerIds.length === 0
          && unknownPlayerIds.every((playerId) => requestEligibleSet.has(playerId) && !recognizedSet.has(playerId))
          && unknownSet.size === unknownPlayerIds.length
          && expectedUnknown.length === unknownPlayerIds.length
          && expectedUnknown.every((playerId, index) => unknownPlayerIds[index] === playerId)
          && unrankedPlayerIds.every((playerId) => requestEligibleSet.has(playerId) && !rankedSet.has(playerId))
          && unrankedSet.size === unrankedPlayerIds.length
          && expectedUnranked.length === unrankedPlayerIds.length
          && expectedUnranked.every((playerId, index) => unrankedPlayerIds[index] === playerId)
          && recognizedPreserved.every((playerId, index) => playerIds[index] === playerId)
        );
        if (validContract) {
          finish(() => resolve(playerIds));
          return;
        }
      }
      finish(() => reject(new Error(
        response.ok === false
          ? typeof response.error === "string" && response.error.trim()
            ? response.error
            : "Draft Lab recommendations could not be calculated."
          : "The Draft Lab recommendation worker returned an invalid response.",
      )));
    }) as EventListener;
    const onError = (() => {
      finish(() => reject(new Error(
        "The Draft Lab recommendation worker stopped unexpectedly.",
      )));
    }) as EventListener;
    const onMessageError = (() => {
      finish(() => reject(new Error(
        "The Draft Lab recommendation worker returned an unreadable response.",
      )));
    }) as EventListener;
    const timeoutId = setTimeout(() => {
      finish(() => reject(new Error("Draft Lab recommendations timed out.")));
    }, Math.max(1, timeoutMs));

    worker.addEventListener("message", onMessage);
    worker.addEventListener("error", onError);
    worker.addEventListener("messageerror", onMessageError);
    try {
      worker.postMessage({
        requestId,
        teamName,
        eligiblePlayerIds: requestEligiblePlayerIds,
        preservedPlayerIds: requestPreservedPlayerIds,
        expectedKeeperCapacity,
      });
    } catch (caught) {
      finish(() => reject(caught instanceof Error
        ? caught
        : new Error("The Draft Lab recommendation request could not be sent.")));
    }
  });
}

export function draftLabAutopopulatePlayerIds(
  teamName: string,
  eligiblePlayerIds: readonly string[],
  preservedPlayerIds: readonly string[],
  expectedKeeperCapacity: number,
): Promise<string[]> {
  return draftLabAutopopulatePlayerIdsWithWorker(
    teamName,
    eligiblePlayerIds,
    preservedPlayerIds,
    expectedKeeperCapacity,
  );
}
